<template>
  <div class="data-report">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>数据报表</span>
        </div>
      </template>

      <!-- 时间筛选 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="开始时间">
          <el-date-picker
            v-model="queryParams.startTime"
            type="datetime"
            placeholder="选择开始时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 200px"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item label="结束时间">
          <el-date-picker
            v-model="queryParams.endTime"
            type="datetime"
            placeholder="选择结束时间"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 200px"
            @change="handleDateChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
        <el-form-item>
          <el-button-group>
            <el-button :type="activeTimeRange === 'month' ? 'primary' : ''" @click="setTimeRange('month')">近一个月</el-button>
            <el-button :type="activeTimeRange === 'quarter' ? 'primary' : ''" @click="setTimeRange('quarter')">近三个月</el-button>
            <el-button :type="activeTimeRange === 'halfYear' ? 'primary' : ''" @click="setTimeRange('halfYear')">近半年</el-button>
            <el-button :type="activeTimeRange === 'year' ? 'primary' : ''" @click="setTimeRange('year')">近一年</el-button>
          </el-button-group>
        </el-form-item>
      </el-form>

      <el-divider />

      <!-- 用户类型统计 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">用户类型统计</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">个人用户（无企业）</div>
                <div class="stat-value">{{ reportData?.userTypeStatistics?.personalUserCount || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">企业用户（有企业）</div>
                <div class="stat-value">{{ reportData?.userTypeStatistics?.companyUserCount || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">总用户数</div>
                <div class="stat-value">{{ reportData?.userTypeStatistics?.totalUserCount || 0 }}</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <!-- 日活/月活用户统计 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">日活/月活用户统计</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-label">日活用户数（DAU）</div>
                <div class="stat-value">{{ reportData?.userActivityStatistics?.dailyActiveUsers || 0 }}</div>
                <div class="stat-detail">今日活跃用户数</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-label">月活用户数（MAU）</div>
                <div class="stat-value">{{ reportData?.userActivityStatistics?.monthlyActiveUsers || 0 }}</div>
                <div class="stat-detail">近30天活跃用户数</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <!-- 业务员模块使用情况统计 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">业务员模块使用情况统计</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-table :data="moduleUsageStatistics" border style="width: 100%">
            <el-table-column prop="module" label="模块名称" width="150" />
            <el-table-column label="小程序使用次数" width="150">
              <template #default="{ row }">
                {{ row.miniUsageCount || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="PC端使用次数" width="150">
              <template #default="{ row }">
                {{ row.pcUsageCount || 0 }}
              </template>
            </el-table-column>
            <el-table-column label="总使用次数" width="150">
              <template #default="{ row }">
                {{ (row.miniUsageCount || 0) + (row.pcUsageCount || 0) }}
              </template>
            </el-table-column>
            <el-table-column label="占比" width="120">
              <template #default="{ row }">
                {{ getModuleUsagePercentage(row) }}%
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>

      <!-- 会员转换率 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">会员转换率</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-label">正式黄金会员转换率</div>
                <div class="stat-value">{{ membershipConversionRate.gold }}%</div>
                <div class="stat-detail">
                  {{ membershipConversionRate.goldCount || 0 }} / {{ reportData?.userTypeStatistics?.totalUserCount || 0 }} 人
                </div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-label">钻石会员转换率</div>
                <div class="stat-value">{{ membershipConversionRate.diamond }}%</div>
                <div class="stat-detail">
                  {{ membershipConversionRate.diamondCount || 0 }} / {{ reportData?.userTypeStatistics?.totalUserCount || 0 }} 人
                </div>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <!-- 会员等级统计 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <div class="card-header">
            <span class="section-title">会员等级统计</span>
            <el-segmented v-model="chartType" :options="chartOptions" size="small" />
          </div>
        </template>
        <div v-loading="loading" class="stat-content">
          <v-chart 
            v-if="membershipChartOption && (chartType === 'bar' ? reportData?.membershipLevelStatistics?.length : reportData?.membershipLevelTimeSeries?.length)" 
            :key="`chart-${chartType}-${reportData?.membershipLevelStatistics?.length || 0}-${reportData?.membershipLevelTimeSeries?.length || 0}`"
            :option="membershipChartOption" 
            style="height: 400px" 
            autoresize
            class="membership-chart"
          />
          <el-empty v-else description="暂无数据" />
        </div>
      </el-card>

      <!-- 会员额度使用情况 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <div class="card-header">
            <span class="section-title">会员额度使用情况（前10名）</span>
            <el-segmented v-model="quotaChartType" :options="quotaChartOptions" size="small" />
          </div>
        </template>
        <div v-loading="loading" class="stat-content">
          <v-chart 
            v-if="quotaChartOption && reportData?.quotaUsageRanking?.length" 
            :key="`quota-chart-${quotaChartType}-${reportData?.quotaUsageRanking?.length || 0}`"
            :option="quotaChartOption" 
            style="height: 400px" 
            autoresize
            class="quota-chart"
          />
          <el-empty v-else description="暂无数据" />
        </div>
      </el-card>

      <!-- 邀请统计 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">邀请统计</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">被邀请的新用户总数</div>
                <div class="stat-value">{{ reportData?.inviteStatistics?.totalInvitedUsers || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">总邀请数</div>
                <div class="stat-value">{{ reportData?.inviteStatistics?.totalInvites || 0 }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item">
                <div class="stat-label">成功邀请数</div>
                <div class="stat-value">{{ reportData?.inviteStatistics?.successInvites || 0 }}</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <!-- 邀请排行榜 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">邀请排行榜</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-table :data="reportData?.inviteRanking || []" border style="width: 100%">
            <el-table-column type="index" label="排名" width="80" />
            <el-table-column prop="inviterName" label="邀请者姓名" width="150" />
            <el-table-column prop="inviterUsername" label="邀请者用户名" width="150" />
            <el-table-column prop="totalInvites" label="总邀请数" width="120" />
            <el-table-column prop="successInvites" label="成功邀请人数" width="120" />
            <el-table-column label="成功率" width="120">
              <template #default="{ row }">
                <span v-if="row.totalInvites > 0">
                  {{ ((row.successInvites / row.totalInvites) * 100).toFixed(2) }}%
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="rechargeCount" label="被邀请者充值次数" width="140">
              <template #default="{ row }">
                <span>{{ row.rechargeCount || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="rechargePeriods" label="充值周期" min-width="200" show-overflow-tooltip>
              <template #default="{ row }">
                <span v-if="row.rechargePeriods">{{ row.rechargePeriods }}</span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="inviteeNames" label="被邀请者姓名" min-width="200" show-overflow-tooltip>
              <template #default="{ row }">
                <span v-if="row.inviteeNames">{{ row.inviteeNames }}</span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="inviteeUsernames" label="被邀请者用户名" min-width="200" show-overflow-tooltip>
              <template #default="{ row }">
                <span v-if="row.inviteeUsernames">{{ row.inviteeUsernames }}</span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>

      <!-- 充值排行榜 -->
      <el-card shadow="never" class="stat-card">
        <template #header>
          <span class="section-title">充值排行榜</span>
        </template>
        <div v-loading="loading" class="stat-content">
          <el-table :data="reportData?.rechargeRanking || []" border style="width: 100%">
            <el-table-column type="index" label="排名" width="80" />
            <el-table-column prop="realName" label="用户姓名" width="150" />
            <el-table-column prop="userName" label="用户名" width="150">
              <template #default="{ row }">
                <span v-if="row.userName && String(row.userName).trim() !== ''">{{ row.userName }}</span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="totalRechargeAmount" label="总充值金额（元）" width="180">
              <template #default="{ row }">
                {{ formatAmount(row.totalRechargeAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="rechargeCount" label="充值次数" width="120" />
          </el-table>
        </div>
      </el-card>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getDataReport } from '../api/dataReport'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'

use([CanvasRenderer, BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent])

const loading = ref(false)
const reportData = ref(null)
const activeTimeRange = ref('month')
const chartType = ref('bar')
const chartOptions = [
  { label: '柱状图', value: 'bar' },
  { label: '折线图', value: 'line' }
]
const quotaChartType = ref('bar')
const quotaChartOptions = [
  { label: '柱状图', value: 'bar' },
  { label: '折线图', value: 'line' }
]

const queryParams = reactive({
  startTime: null,
  endTime: null
})

// 格式化日期为 YYYY-MM-DD HH:mm:ss
const formatDateTime = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

// 设置时间范围
const setTimeRange = (range) => {
  activeTimeRange.value = range
  const now = new Date()
  let startTime = new Date()

  switch (range) {
    case 'month':
      startTime.setMonth(now.getMonth() - 1)
      break
    case 'quarter':
      startTime.setMonth(now.getMonth() - 3)
      break
    case 'halfYear':
      startTime.setMonth(now.getMonth() - 6)
      break
    case 'year':
      startTime.setFullYear(now.getFullYear() - 1)
      break
  }

  queryParams.startTime = formatDateTime(startTime)
  queryParams.endTime = formatDateTime(now)
  loadData()
}

const handleSearch = () => {
  // 点击查询时，不清除快捷筛选的选中状态，直接使用当前的时间参数查询
  loadData()
}

// 当用户手动修改日期选择器时，清除快捷筛选的选中状态
const handleDateChange = () => {
  activeTimeRange.value = null
}

const handleReset = () => {
  // 重置时默认选中"近一个月"并查询
  setTimeRange('month')
}

const loadData = async () => {
  loading.value = true
  try {
    const params = {}
    if (queryParams.startTime) {
      params.startTime = queryParams.startTime
    }
    if (queryParams.endTime) {
      params.endTime = queryParams.endTime
    }

    const { data, code, msg, success } = await getDataReport(params)
    if ((code && code !== '000000') || success === false) {
      throw new Error(msg || '获取数据报表失败')
    }
    reportData.value = data || null
    
    // 调试：打印充值排行榜数据
    if (data && data.rechargeRanking) {
      console.log('充值排行榜数据:', JSON.stringify(data.rechargeRanking, null, 2))
    }
  } catch (error) {
    console.error('获取数据报表失败:', error)
    ElMessage.error(error.message || '获取数据报表失败')
  } finally {
    loading.value = false
  }
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toFixed(2)
}

// 业务员模块使用情况统计
const moduleUsageStatistics = computed(() => {
  return reportData.value?.moduleUsageStatistics || []
})

// 计算模块使用情况百分比
const getModuleUsagePercentage = (row) => {
  const totalUsage = (row.miniUsageCount || 0) + (row.pcUsageCount || 0)
  const allModulesTotal = moduleUsageStatistics.value.reduce((sum, item) => {
    return sum + (item.miniUsageCount || 0) + (item.pcUsageCount || 0)
  }, 0)
  
  if (allModulesTotal === 0) return '0.00'
  return ((totalUsage / allModulesTotal) * 100).toFixed(2)
}

// 计算会员转换率
const membershipConversionRate = computed(() => {
  const totalUsers = reportData.value?.userTypeStatistics?.totalUserCount || 0
  const membershipStats = reportData.value?.membershipLevelStatistics || []
  
  // 查找正式黄金会员（GOLD）和钻石会员（DIAMOND）
  let goldCount = 0
  let diamondCount = 0
  
  membershipStats.forEach(item => {
    if (item.levelCode === 'GOLD') {
      goldCount = item.userCount || 0
    } else if (item.levelCode === 'DIAMOND') {
      diamondCount = item.userCount || 0
    }
  })
  
  // 计算转换率（保留2位小数）
  const goldRate = totalUsers > 0 ? ((goldCount / totalUsers) * 100).toFixed(2) : '0.00'
  const diamondRate = totalUsers > 0 ? ((diamondCount / totalUsers) * 100).toFixed(2) : '0.00'
  
  return {
    gold: goldRate,
    goldCount: goldCount,
    diamond: diamondRate,
    diamondCount: diamondCount
  }
})

// 会员等级统计图表配置
const membershipChartOption = computed(() => {
  // 定义颜色方案，协调美观
  const colors = {
    'BRONZE': '#cd7f32',
    'SILVER': '#c0c0c0',
    'GOLD': '#ffd700',
    'DIAMOND': '#b9f2ff',
    'TRIAL_GOLD': '#ffa500',
    'FREE': '#909399'
  }

  // 柱状图：显示当前统计
  if (chartType.value === 'bar') {
    const statistics = reportData.value?.membershipLevelStatistics || []
    if (!statistics.length) return null

    const levelNames = statistics.map(item => item.levelName)
    const userCounts = statistics.map(item => item.userCount || 0)

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          const param = params[0]
          return `${param.name}<br/>${param.seriesName}: ${param.value} 人`
        }
      },
      legend: {
        show: false
      },
      grid: {
        left: '10%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: levelNames,
        axisLabel: {
          rotate: 0,
          fontSize: 12,
          color: '#606266'
        },
        axisLine: {
          lineStyle: {
            color: '#e4e7ed'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '用户数量',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: '#606266',
          fontSize: 12
        },
        axisLabel: {
          color: '#606266',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: '#e4e7ed'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#f0f2f5',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: '用户数量',
          type: 'bar',
          data: userCounts,
          itemStyle: {
            color: (params) => {
              const levelCode = statistics[params.dataIndex]?.levelCode
              return colors[levelCode] || '#5470c6'
            },
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            color: '#303133',
            fontSize: 12,
            formatter: '{c}'
          }
        }
      ]
    }
  }

  // 折线图：显示时间序列数据
  const timeSeries = reportData.value?.membershipLevelTimeSeries || []
  if (!timeSeries.length) return null

  // 获取所有唯一的日期
  const dates = [...new Set(timeSeries.map(item => item.date))].sort()
  
  // 获取所有等级
  const levelMap = new Map()
  timeSeries.forEach(item => {
    if (!levelMap.has(item.levelCode)) {
      levelMap.set(item.levelCode, {
        code: item.levelCode,
        name: item.levelName
      })
    }
  })
  const levels = Array.from(levelMap.values())

  // 为每个等级构建数据序列
  const series = levels.map(level => {
    const data = dates.map(date => {
      const item = timeSeries.find(ts => ts.date === date && ts.levelCode === level.code)
      return item ? (item.userCount || 0) : 0
    })
    return {
      name: level.name,
      type: 'line',
      data: data,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: {
        width: 2
      },
      itemStyle: {
        color: colors[level.code] || '#5470c6'
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: getColorWithAlpha(colors[level.code] || '#5470c6', 0.3) },
            { offset: 1, color: getColorWithAlpha(colors[level.code] || '#5470c6', 0.05) }
          ]
        }
      },
      label: {
        show: false
      }
    }
  })

  // 格式化日期显示（如果日期太多，只显示部分）
  const formatDates = dates.map(date => {
    const d = new Date(date)
    const month = d.getMonth() + 1
    const day = d.getDate()
    // 如果日期太多，只显示月-日，否则显示完整日期
    if (dates.length > 30) {
      return `${month}/${day}`
    }
    return `${month}-${day}`
  })

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: (params) => {
        let result = `${params[0].axisValue}<br/>`
        params.forEach(param => {
          result += `${param.marker}${param.seriesName}: ${param.value} 人<br/>`
        })
        return result
      }
    },
    legend: {
      data: levels.map(l => l.name),
      top: 10,
      textStyle: {
        fontSize: 12,
        color: '#606266'
      }
    },
    grid: {
      left: '10%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: false
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: formatDates,
      axisLabel: {
        rotate: dates.length > 30 ? 45 : 0,
        fontSize: 11,
        color: '#606266',
        formatter: (value, index) => {
          // 如果日期太多，每隔几个显示一个
          if (dates.length > 30 && index % Math.ceil(dates.length / 15) !== 0) {
            return ''
          }
          return value
        }
      },
      axisLine: {
        lineStyle: {
          color: '#e4e7ed'
        }
      }
    },
    yAxis: {
      type: 'value',
      name: '用户数量',
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        color: '#606266',
        fontSize: 12
      },
      axisLabel: {
        color: '#606266',
        fontSize: 12
      },
      axisLine: {
        lineStyle: {
          color: '#e4e7ed'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f0f2f5',
          type: 'dashed'
        }
      }
    },
    series: series
  }
})

// 辅助函数：给颜色添加透明度
const getColorWithAlpha = (color, alpha) => {
  // 简单的颜色转换，支持hex颜色
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16)
    const g = parseInt(color.slice(3, 5), 16)
    const b = parseInt(color.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return color
}

// 会员额度使用情况图表配置
const quotaChartOption = computed(() => {
  const ranking = reportData.value?.quotaUsageRanking || []
  if (!ranking.length) return null

  // 柱状图：显示名片扫描次数和附件上传次数对比
  if (quotaChartType.value === 'bar') {
    const userNames = ranking.map(item => {
      if (item.realName && item.realName !== '微信用户') {
        return item.realName
      }
      return item.userName || `用户${item.userId}`
    })
    const cardScanCounts = ranking.map(item => item.cardScanCount || 0)
    const attachmentCounts = ranking.map(item => item.attachmentCount || 0)
    const totalUsages = ranking.map(item => item.totalUsage || 0)

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          let result = `${params[0].name}<br/>`
          params.forEach(param => {
            result += `${param.marker}${param.seriesName}: ${param.value} 次<br/>`
          })
          return result
        }
      },
      legend: {
        data: ['名片扫描次数', '附件上传次数', '总使用额度'],
        top: 10,
        textStyle: {
          fontSize: 12,
          color: '#606266'
        }
      },
      grid: {
        left: '10%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: false
      },
      xAxis: {
        type: 'category',
        data: userNames,
        axisLabel: {
          rotate: 45,
          fontSize: 12,
          color: '#606266',
          interval: 0
        },
        axisLine: {
          lineStyle: {
            color: '#e4e7ed'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '使用次数',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: '#606266',
          fontSize: 12
        },
        axisLabel: {
          color: '#606266',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: '#e4e7ed'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#f0f2f5',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: '名片扫描次数',
          type: 'bar',
          data: cardScanCounts,
          itemStyle: {
            color: '#409eff',
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            color: '#303133',
            fontSize: 11,
            formatter: '{c}'
          }
        },
        {
          name: '附件上传次数',
          type: 'bar',
          data: attachmentCounts,
          itemStyle: {
            color: '#67c23a',
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            color: '#303133',
            fontSize: 11,
            formatter: '{c}'
          }
        },
        {
          name: '总使用额度',
          type: 'line',
          data: totalUsages,
          itemStyle: {
            color: '#e6a23c'
          },
          lineStyle: {
            width: 2
          },
          symbol: 'circle',
          symbolSize: 6,
          label: {
            show: true,
            position: 'top',
            color: '#303133',
            fontSize: 11,
            formatter: '{c}'
          }
        }
      ]
    }
  }

  // 折线图：显示总使用额度趋势
  const userNames = ranking.map(item => {
    if (item.realName && item.realName !== '微信用户') {
      return item.realName
    }
    return item.userName || `用户${item.userId}`
  })
  const cardScanCounts = ranking.map(item => item.cardScanCount || 0)
  const attachmentCounts = ranking.map(item => item.attachmentCount || 0)
  const totalUsages = ranking.map(item => item.totalUsage || 0)

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: (params) => {
        let result = `${params[0].name}<br/>`
        params.forEach(param => {
          result += `${param.marker}${param.seriesName}: ${param.value} 次<br/>`
        })
        return result
      }
    },
    legend: {
      data: ['名片扫描次数', '附件上传次数', '总使用额度'],
      top: 10,
      textStyle: {
        fontSize: 12,
        color: '#606266'
      }
    },
    grid: {
      left: '10%',
      right: '4%',
      bottom: '15%',
      top: '15%',
      containLabel: false
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: userNames,
      axisLabel: {
        rotate: 45,
        fontSize: 11,
        color: '#606266',
        interval: 0
      },
      axisLine: {
        lineStyle: {
          color: '#e4e7ed'
        }
      }
    },
    yAxis: {
      type: 'value',
      name: '使用次数',
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        color: '#606266',
        fontSize: 12
      },
      axisLabel: {
        color: '#606266',
        fontSize: 12
      },
      axisLine: {
        lineStyle: {
          color: '#e4e7ed'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f0f2f5',
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: '名片扫描次数',
        type: 'line',
        data: cardScanCounts,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: '#409eff'
        },
        itemStyle: {
          color: '#409eff'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: getColorWithAlpha('#409eff', 0.3) },
              { offset: 1, color: getColorWithAlpha('#409eff', 0.05) }
            ]
          }
        }
      },
      {
        name: '附件上传次数',
        type: 'line',
        data: attachmentCounts,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: '#67c23a'
        },
        itemStyle: {
          color: '#67c23a'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: getColorWithAlpha('#67c23a', 0.3) },
              { offset: 1, color: getColorWithAlpha('#67c23a', 0.05) }
            ]
          }
        }
      },
      {
        name: '总使用额度',
        type: 'line',
        data: totalUsages,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#e6a23c'
        },
        itemStyle: {
          color: '#e6a23c'
        },
        label: {
          show: true,
          position: 'top',
          color: '#303133',
          fontSize: 11,
          formatter: '{c}'
        }
      }
    ]
  }
})

onMounted(() => {
  // 默认查询近一个月的数据
  setTimeRange('month')
})
</script>

<style scoped>
.data-report {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 16px;
}

.stat-card {
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.stat-content {
  padding: 10px 0;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.stat-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #409eff;
}

.stat-detail {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.text-muted {
  color: #909399;
}

.membership-chart {
  width: 100%;
}

.quota-chart {
  width: 100%;
}
</style>

